import type { EncoderName, RecordingConfigType, RecordingEncoderTuning } from "domains/managingJson/store/types"

const NVIDIA_FAMILY_DEFAULT: RecordingEncoderTuning = {
  preset: "HIGH_QUALITY",
  rateControl: "RC_CBR",
  keyframeIntervalSec: 2,
}

const AMD_FAMILY_DEFAULT: RecordingEncoderTuning = {
  preset: "QUALITY",
  rateControl: "RC_CBR",
  keyframeIntervalSec: 2,
}

const X264_FAMILY_DEFAULT: RecordingEncoderTuning = {
  preset: "VERYFAST",
  rateControl: "RC_CBR",
  keyframeIntervalSec: 2,
}

const INTEL_FAMILY_DEFAULT: RecordingEncoderTuning = {
  preset: "",
  rateControl: "",
  keyframeIntervalSec: 2,
}

export const DEFAULT_PER_ENCODER: Record<EncoderName, RecordingEncoderTuning> = {
  NVIDIA_NVENC_NEW: NVIDIA_FAMILY_DEFAULT,
  NVIDIA_NVENC: NVIDIA_FAMILY_DEFAULT,
  NVIDIA_NVENC_HEVC: NVIDIA_FAMILY_DEFAULT,
  NVIDIA_NVENC_AV1: NVIDIA_FAMILY_DEFAULT,
  AMD_AMF: AMD_FAMILY_DEFAULT,
  AMD_AMF_HEVC: AMD_FAMILY_DEFAULT,
  X264: X264_FAMILY_DEFAULT,
  INTEL: INTEL_FAMILY_DEFAULT,
}

export const DEFAULT_RECORDING_CONFIG: RecordingConfigType = {
  enabled: true,
  video: {
    resolutionMode: "forced",
    fps: 30,
    width: 1920,
    height: 1080,
    maxKbps: 20000,
    autoCalcKbps: false,
    captureMouseCursor: "none",
  },
  audio: {
    gameEnabled: true,
    gameVolume: 100,
    micEnabled: false,
    micVolume: 0,
    filteredCaptureEnabled: true,
  },
  encoders: {
    priority: ["NVIDIA_NVENC_NEW", "NVIDIA_NVENC", "AMD_AMF", "INTEL", "X264"],
    perEncoder: DEFAULT_PER_ENCODER,
  },
  collector: {
    heartbeatIntervalMs: 5000,
    mousePosHz: 30,
  },
  storage: {
    maxFilePartSizeBytes: 1_073_741_824, // 1 GiB default
    quotaPctOfFree: 40,
    minFreeGbToRecord: 5,
  },
  markerOverlay: {
    enabled: false,
    durationMs: 500,
    prewarmDelayMs: 3000,
    postStartDelayMs: 1500,
    postEndDelayMs: 1000,
  },
  pointerTracking: {
    enabled: true,
    clientSideProcessing: true,
    // Default to the efficient (binary) tracker — cloud sync pipeline trims it end-to-end.
    // Admin can flip to 'inefficient' (NDJSON) for debugging when needed.
    options: { type: "efficient" },
  },
  networkInspector: {
    enabled: true,
    enableEtw: false,
    etwCountries: [],
    etwAccountIds: [],
    etwEmailDomains: [],
  },
  upload: {
    inGameSpeedPct: 20,
    partSizeBytes: 10 * 1024 * 1024, // 10 MiB
    maxPartRetries: 6,
    retryBackoffMs: 2_000,
    retryBackoffCapMs: 60_000,
    s3AbortWindowMs: 7 * 24 * 60 * 60 * 1000, // 7 d
    maxJobRetries: 3,
    jobRetryBaseDelayMs: 30_000,
    jobRetryCapMs: 300_000,
    maxLaunchRevives: 6,
    networkHoldEnabled: true,
    networkHoldProbeMs: 20_000,
    maxOfflineHolds: 10,
    maxOfflineHoldMs: 30 * 60 * 1000, // 30 min
  },
  timeSync: { sntpServer: "time.aws.com" },
  depth: {
    enabled: false,
    games: {},
    fps: 30,
    width: 0,
    height: 0,
    minFreeGb: 10,
    chunkMinutes: 10,
    extraAntiCheat: [],
  },
}
